import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    Fade,
    IconButton,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useApsDiscoveryStore, { hasSeenApsDiscoveryModal } from '@/stores/useApsDiscoveryStore';

const APS_DISCOVERY_VIDEO_SRC = `${import.meta.env.VITE_APP_API_URL}/marketing/aps-data-extraction.mp4`;
const DISCOVERY_MODAL_SHOW_DELAY_MS = 1500;

function playMutedVideo(element: HTMLVideoElement | null) {
    if (!element) return;
    element.muted = true;
    void element.play().catch(() => {
        // Autoplay can still be blocked; controls remain available.
    });
}

export default function ApsDataExtractionDiscoveryModal() {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const isVideoModalOpen = useApsDiscoveryStore((state) => state.isVideoModalOpen);
    const showVideoModal = useApsDiscoveryStore((state) => state.showVideoModal);
    const closeVideoModal = useApsDiscoveryStore((state) => state.closeVideoModal);
    const startTryItNow = useApsDiscoveryStore((state) => state.startTryItNow);

    useEffect(() => {
        if (hasSeenApsDiscoveryModal()) {
            return;
        }

        const timeoutId = window.setTimeout(() => {
            if (hasSeenApsDiscoveryModal()) {
                return;
            }
            showVideoModal();
        }, DISCOVERY_MODAL_SHOW_DELAY_MS);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [showVideoModal]);

    const handleTryItNow = () => {
        startTryItNow();
        if (location.pathname !== '/' && location.pathname !== '/prospects') {
            navigate('/prospects');
        }
    };

    return (
        <Dialog
            open={isVideoModalOpen}
            disableEscapeKeyDown
            TransitionComponent={Fade}
            transitionDuration={{ enter: 500, exit: 250 }}
            maxWidth={false}
            aria-labelledby="aps-data-extraction-discovery-title"
            PaperProps={{
                sx: {
                    borderRadius: '10px',
                    overflow: 'hidden',
                    width: 'min(1280px, 94vw)',
                    maxHeight: '92vh',
                },
            }}
        >
            <DialogTitle
                id="aps-data-extraction-discovery-title"
                sx={{
                    position: 'relative',
                    pr: 6,
                    pb: 1,
                    fontSize: 24,
                    fontWeight: 600,
                    lineHeight: '32px',
                    color: theme.palette.neutral.black,
                }}
            >
                Agreement of Purchase and Sale Data Extraction
                <IconButton
                    aria-label="Close"
                    onClick={closeVideoModal}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: theme.palette.neutral.N_500,
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ overflow: 'auto' }}>
                <Stack spacing={2.5} sx={{ pt: 0.5, pb: 1 }}>
                    <Typography
                        variant="h2"
                        sx={{ color: theme.palette.neutral.navy, fontWeight: 600 }}
                    >
                        Already have the APS? Let Lexo do the re-keying.
                    </Typography>
                    <Typography variant="body1" sx={{ color: theme.palette.neutral.N_700 }}>
                        Drag and drop an Agreement of Purchase and Sale into Lexo. Lexo extracts
                        information already contained in the APS and carries applicable data into
                        your intake, reducing repetitive data entry.
                    </Typography>
                    <Box
                        sx={{
                            borderRadius: '8px',
                            overflow: 'hidden',
                            backgroundColor: theme.palette.neutral.black,
                            aspectRatio: '16 / 9',
                            maxHeight: '62vh',
                        }}
                    >
                        <video
                            src={APS_DISCOVERY_VIDEO_SRC}
                            autoPlay
                            muted
                            loop
                            playsInline
                            controls
                            preload="auto"
                            ref={playMutedVideo}
                            style={{
                                width: '100%',
                                height: '100%',
                                display: 'block',
                                objectFit: 'contain',
                            }}
                        />
                    </Box>
                    <Button
                        variant="contained"
                        onClick={handleTryItNow}
                        size="large"
                        sx={{ textTransform: 'none', py: 1.25 }}
                    >
                        Try it now
                    </Button>
                    <Stack
                        direction="row"
                        spacing={0.75}
                        alignItems="flex-start"
                        justifyContent="center"
                    >
                        <InfoOutlinedIcon
                            sx={{ fontSize: 16, mt: '1px', color: theme.palette.neutral.N_500 }}
                        />
                        <Typography variant="body2" sx={{ textAlign: 'left' }}>
                            Extracted information should be reviewed for accuracy before you send
                            the intake.
                        </Typography>
                    </Stack>
                </Stack>
            </DialogContent>
        </Dialog>
    );
}
